"use client";

import Link from "next/link";

import { FadeIn, HomeEyebrow, HomeHeading, HomeLead, HomeSection } from "~/components/siteglue/home/section-shell";
import { AUDIT_ARO_HREF, WORDPRESS_PLUGIN_HREF } from "~/lib/siteglue/landing-redesign/copy";
import {
  WORDPRESS_PLUGIN_CURRENT,
  WORDPRESS_PLUGIN_PLANNED,
} from "~/lib/siteglue/wordpress-plugin";

const PLUGIN_STEPS = [
  {
    n: "01",
    label: "Install the plugin",
    body: "Copy the SiteGlue plugin into WordPress and activate it. Manual install or WP-CLI on supported hosts.",
  },
  {
    n: "02",
    label: "Paste your SiteGlue MCP URL",
    body: "Add the MCP endpoint from your SiteGlue project. Trish never asks for a WordPress administrator password.",
  },
  {
    n: "03",
    label: "Publish agent-ready files",
    body: "The plugin can publish llms.txt, an MCP card, JSON-LD, robots hints, and contact facts from your business settings.",
  },
  {
    n: "04",
    label: "Recheck your ARO audit",
    body: "Run the ARO audit again to see which supported checks changed. The plugin does not guarantee a higher score.",
  },
] as const;

export function SiteGlueHomeWordpressPlugin() {
  return (
    <HomeSection id="wordpress-plugin" tone="white" className="py-16 sm:py-20">
      <div className="grid items-start gap-10 lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
        <FadeIn>
          <HomeEyebrow>WordPress Plugin</HomeEyebrow>
          <HomeHeading className="mt-4 text-4xl font-semibold sm:text-5xl">
            Publish the files AI agents need
          </HomeHeading>
          <HomeLead>
            A WordPress plugin that helps your site publish supported Agent Readiness files — without
            giving SiteGlue your WordPress admin password.
          </HomeLead>
          <ol className="relative mt-10 space-y-8 border-l border-slate-200 pl-6">
            {PLUGIN_STEPS.map((step) => (
              <li key={step.n} className="relative">
                <span
                  className="absolute -left-[1.85rem] top-1.5 h-2.5 w-2.5 rounded-full bg-[#4f81bd]"
                  aria-hidden
                />
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                  {step.n} {step.label}
                </p>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{step.body}</p>
              </li>
            ))}
          </ol>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link
              href={WORDPRESS_PLUGIN_HREF}
              className="text-sm font-semibold text-[#4f81bd] hover:underline"
            >
              WordPress plugin details
            </Link>
            <Link href={AUDIT_ARO_HREF} className="text-sm font-semibold text-slate-700 hover:underline">
              Run My Free ARO Audit
            </Link>
          </div>
        </FadeIn>
        <FadeIn delay={0.08} className="min-w-0">
          <WordpressPluginPreview />
        </FadeIn>
      </div>
    </HomeSection>
  );
}

function WordpressPluginPreview() {
  return (
    <div className="overflow-hidden rounded-3xl border border-slate-200 bg-[#111827] shadow-[0_24px_60px_rgba(15,23,42,0.18)]">
      <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
          SiteGlue · WordPress
        </p>
        <p className="text-xs text-slate-500">Published files</p>
      </div>
      <div className="grid gap-0 sm:grid-cols-[11rem_minmax(0,1fr)]">
        <aside className="hidden border-r border-white/10 p-4 sm:block">
          <p className="text-xs font-semibold text-white">SiteGlue</p>
          <p className="mt-3 text-xs text-slate-500">Settings</p>
          <p className="mt-2 rounded-md bg-[#4f81bd] px-2 py-1.5 text-xs font-medium text-white">
            Agent files
          </p>
          <p className="mt-2 px-2 py-1.5 text-xs text-slate-400">Business facts</p>
          <p className="px-2 py-1.5 text-xs text-slate-400">MCP URL</p>
        </aside>
        <div className="p-5">
          <p className="text-sm font-semibold text-white">What the plugin publishes today</p>
          <p className="mt-1 text-xs text-slate-400">
            Operator pastes the SiteGlue MCP URL. No admin password in chat.
          </p>
          <ul className="mt-4 space-y-2">
            {WORDPRESS_PLUGIN_CURRENT.slice(0, 6).map((item) => (
              <li
                key={item}
                className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-slate-200"
              >
                {item}
              </li>
            ))}
          </ul>
          <p className="mt-4 text-xs text-slate-500">
            Planned, not assumed live: {WORDPRESS_PLUGIN_PLANNED[0]}
          </p>
        </div>
      </div>
    </div>
  );
}
